import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, X as XIcon } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Customer Story Capture — surfaces a weekly prompt on the dashboard
 * for users with the public blog enabled who haven't shipped a case
 * study in the last 7 days. One tap → case-study creator with the
 * brand already known.
 *
 * Renders nothing when the backend returns show:false.
 */
const StoryCapturePrompt = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public-blog/story-prompt`)
      .then((r) => { if (r.data?.show) setPrompt(r.data); })
      .catch(() => {});
  }, []);

  const dismiss = async () => {
    setDismissing(true);
    try { await axios.post(`${API_URL}/api/public-blog/story-prompt/dismiss`, {}); } catch {}
    setPrompt(null);
  };

  if (!prompt) return null;

  return (
    <div data-testid="story-capture-prompt"
      className="mb-6 rounded-2xl p-5 sm:p-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(200,241,53,0.08), rgba(200,241,53,0.02))',
        border: '1px solid rgba(200,241,53,0.32)',
      }}>
      <div style={{
        position: 'absolute', top: 0, left: 24, right: 24, height: 1,
        background: 'linear-gradient(90deg, transparent, rgba(200,241,53,0.5), transparent)',
      }} />

      <button onClick={dismiss} disabled={dismissing} data-testid="story-capture-dismiss"
        aria-label="Dismiss this week's prompt"
        style={{ position: 'absolute', top: 14, right: 14,
          background: 'transparent', border: 'none', cursor: dismissing ? 'wait' : 'pointer',
          width: 28, height: 28, borderRadius: 8, color: 'rgba(245,240,232,0.4)',
          display: 'grid', placeItems: 'center',
        }}>
        <XIcon size={14} weight="bold" />
      </button>

      <div className="flex items-start gap-3 mb-3">
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: 'rgba(200,241,53,0.12)', border: '1px solid rgba(200,241,53,0.32)',
          display: 'grid', placeItems: 'center', flexShrink: 0,
        }}>
          <Trophy size={20} weight="duotone" color="#c8f135" />
        </div>
        <div className="flex-1 min-w-0">
          <p style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 9.5, color: '#c8f135', letterSpacing: '0.24em',
            textTransform: 'uppercase', fontWeight: 700, marginBottom: 8,
          }}>This week's win</p>
          <h3 className="text-lg sm:text-xl font-bold mb-2" style={{
            fontFamily: "'Syne', system-ui, sans-serif",
            color: '#f5f0e8', letterSpacing: '-0.018em', lineHeight: 1.2,
          }}>{prompt.headline}</h3>
          <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(245,240,232,0.7)' }}>
            {prompt.body}
          </p>
        </div>
      </div>

      <button onClick={() => navigate(prompt.cta_route)}
        data-testid="story-capture-cta"
        style={{
          background: '#c8f135', color: '#04050a', border: 'none',
          padding: '11px 22px', borderRadius: 999, cursor: 'pointer',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
          display: 'inline-flex', alignItems: 'center', gap: 6,
        }}>
        {prompt.cta_label}
      </button>
    </div>
  );
};

export default StoryCapturePrompt;
